import { ETHERAL_721_FACTORY_ABI } from "./ethereals-721-factory-abi";
import { ETHEREAL_721_ABI } from "./ethereal-721-abi";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "next/dist/server/api-utils";
import { useWriteContract } from "@/hooks/useWriteContract.hook";
import { useMemo } from "react";
import toast from "react-hot-toast";
import { isMainnet } from "@/constants/flags.constant";

const FACTORY_ADDRESS = {
  // https://blessnet.calderaexplorer.xyz/address/0x00000000006c4E67F7C441b6Ff95a1302E0d5740
  mainnet: "0x00000000006c4E67F7C441b6Ff95a1302E0d5740",
  // https://blessnet-sepolia-testnet.explorer.caldera.xyz/address/0x00000000005a4763D1fe68ebF4b6601E6C129dDf
  sepolia: "0x00000000005a4763D1fe68ebF4b6601E6C129dDf",
};

interface UseEtherealProps {
  chainId: number;
  contractAddress: string;
}

/**
 * @description This hook is used to create and manage an ethereal mirror.
 * @param chainId - The chain ID of the contract.
 * @param contractAddress - The address of the contract.
 * @returns An object containing the token metadata, account address, account exists, deposit balance, is loading balance, is creating, is depositing, and error.
 */
export function useEthereal({ chainId, contractAddress }: UseEtherealProps) {
  const network = isMainnet ? "sepolia" : "sepolia";
  const key = useMemo(
    () => ["ethereals", chainId, contractAddress],
    [chainId, contractAddress],
  );
  const etherealQuery = useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await fetch(
        `/api/blessnet/ethereals/${chainId}/${contractAddress}?network=${network}`,
      );
      if (res.status !== 200)
        throw new ApiError(res.status, "Failed to fetch ethereal data");
      const data = await res.json();
      return data.ethereal;
    },
    enabled: Boolean(contractAddress),
    // we 404 when not found, no need to retry for that
    retry: false,
  });
  const queryClient = useQueryClient();
  const { ethereal: etherealAddress } = etherealQuery.data || {};

  // const balanceQuery = useReadContract({
  //   address: etherealAddress,
  //   abi: ETHEREAL_721_ABI,
  //   functionName: "getDeposit",
  //   query: {
  //     enabled: Boolean(etherealAddress),
  //   },
  // });

  const { writeContractAsync } = useWriteContract();
  const handleSubmit = async ({ fundingAmount }) => {
    let opts: any = {};
    let toastMsg;
    if (etherealAddress) {
      opts = {
        address: etherealAddress,
        abi: ETHEREAL_721_ABI,
        functionName: "addDeposit",
        value: fundingAmount,
      };
      toastMsg = "Top up successful!";
    } else {
      opts = {
        address: FACTORY_ADDRESS[network],
        abi: ETHERAL_721_FACTORY_ABI,
        functionName: "createAccount",
        args: [BigInt(chainId), contractAddress as `0x${string}`],
        value: fundingAmount,
      };
    }
    await writeContractAsync(opts);
    // the API will take a long time to catch this change so we are optimistic about it 🤞
    queryClient.setQueryData(key, {
      ethereal: undefined,
      balance: (
        BigInt(etherealQuery.data?.balance || "0") + fundingAmount
      ).toString(),
      status: !etherealQuery.data ? "pending" : "active",
    });
    toast.success(toastMsg);
  };

  return {
    etherealQuery,
    handleSubmit,
  };
}
